import { useState } from "react";
import { Link } from "react-router-dom";
import {
    Plus, TrendingUp, TrendingDown, ArrowDownToLine, ArrowUpToLine, X,
    Wallet, Receipt,
} from "lucide-react";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

/**
 * MobileCashFab — floating "+" pill on the bottom-right, visible only
 * on mobile viewports (< sm). Tap opens a bottom sheet with thumb-sized
 * action tiles that deep-link to the right module with `?add=<kind>` so
 * the entry dialog auto-opens.
 *
 * Variants:
 *   • "cash" (default) — Sale · Expense · Deposit · Withdrawal.
 *     Meant for the Cash Desk page — cash-only flows, no cards.
 *   • "all"            — Cash sale · Cash expense · Card expense ·
 *     Income · Cash deposit · Cash withdrawal.
 *     Meant for pages where the user isn't in a single-purpose module
 *     (Home, dashboards) and needs the fastest path to any inflow /
 *     outflow entry.
 *
 * Positioned `bottom-24 z-[60]` so it clears the mobile BottomNav
 * (bottom-0, ~56px) and any floating chat pill.
 */
const CASH_ACTIONS = [
    { key: "sale",       label: "Sale",       sub: "Money in",     tone: "emerald", icon: TrendingUp,      to: "/cash-desk?tab=ledger&add=sale" },
    { key: "expense",    label: "Expense",    sub: "Money out",    tone: "rose",    icon: TrendingDown,    to: "/cash-desk?tab=ledger&add=expense" },
    { key: "deposit",    label: "Deposit",    sub: "To bank",      tone: "blue",    icon: ArrowDownToLine, to: "/cash-desk?tab=ledger&add=deposit" },
    { key: "withdrawal", label: "Withdrawal", sub: "From bank",    tone: "stone",   icon: ArrowUpToLine,   to: "/cash-desk?tab=ledger&add=withdrawal" },
];

const ALL_ACTIONS = [
    { key: "cash-sale",   label: "Cash sale",     sub: "Money in",   tone: "emerald", icon: TrendingUp,      to: "/cash-desk?tab=ledger&add=sale" },
    { key: "cash-exp",    label: "Cash expense",  sub: "Money out",  tone: "rose",    icon: TrendingDown,    to: "/cash-desk?tab=ledger&add=expense" },
    { key: "card-exp",    label: "Card expense",  sub: "Log a spend",tone: "amber",   icon: Receipt,         to: "/quick-entry?receipt=1" },
    { key: "income",      label: "Income",       sub: "Salary · fees", tone: "blue",  icon: Wallet,          to: "/income?add=1" },
    { key: "cash-dep",    label: "Deposit",       sub: "Cash → bank",tone: "stone",   icon: ArrowDownToLine, to: "/cash-desk?tab=ledger&add=deposit" },
    { key: "cash-wd",     label: "Withdrawal",    sub: "Bank → cash",tone: "stone",   icon: ArrowUpToLine,   to: "/cash-desk?tab=ledger&add=withdrawal" },
];

export default function MobileCashFab({ variant = "cash", label = "Cash" }) {
    const [open, setOpen] = useState(false);
    const actions = variant === "all" ? ALL_ACTIONS : CASH_ACTIONS;
    const effectiveLabel = variant === "all" ? "Add" : label;

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label={variant === "all" ? "Quick add entry" : "Log a cash entry"}
                data-testid="cash-fab-mobile"
                className="sm:hidden fixed bottom-24 right-5 z-[60] inline-flex items-center gap-1.5 rounded-full px-4 py-3 bg-stone-900 text-white shadow-xl shadow-stone-900/25 active:scale-95 transition-transform"
            >
                <Plus size={16} strokeWidth={2.4} />
                <span className="text-sm font-semibold tracking-wide">{effectiveLabel}</span>
            </button>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent
                    side="bottom"
                    className="sm:hidden rounded-t-2xl px-5 pt-6 pb-8 max-h-[85vh]"
                    data-testid="cash-fab-sheet"
                >
                    <SheetHeader className="text-left mb-4">
                        <SheetTitle className="font-display text-2xl leading-none">
                            {variant === "all" ? "Quick add." : "Log a cash entry."}
                        </SheetTitle>
                        <SheetDescription className="text-xs">
                            Tap one below — the full form opens next with amount and remarks in reach.
                        </SheetDescription>
                    </SheetHeader>

                    <div className="grid grid-cols-2 gap-3" data-testid="cash-fab-actions">
                        {actions.map((a) => (
                            <FabAction
                                key={a.key}
                                to={a.to}
                                onClick={() => setOpen(false)}
                                icon={a.icon}
                                label={a.label}
                                sub={a.sub}
                                tone={a.tone}
                                testid={`cash-fab-${a.key}`}
                            />
                        ))}
                    </div>

                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="mt-5 w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-stone-200 py-3 text-sm text-stone-600"
                        data-testid="cash-fab-cancel"
                    >
                        <X size={14} /> Cancel
                    </button>
                </SheetContent>
            </Sheet>
        </>
    );
}

const TONES = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    rose:    "bg-rose-50 border-rose-200 text-rose-900",
    blue:    "bg-blue-50 border-blue-200 text-blue-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    stone:   "bg-stone-50 border-stone-200 text-stone-900",
};
const TONE_ICONS = {
    emerald: "text-emerald-700",
    rose:    "text-rose-700",
    blue:    "text-blue-700",
    amber:   "text-amber-700",
    stone:   "text-stone-600",
};

function FabAction({ to, onClick, icon: Icon, label, sub, tone, testid }) {
    return (
        <Link
            to={to}
            onClick={onClick}
            data-testid={testid}
            className={`flex flex-col items-start justify-between rounded-xl border px-4 py-5 min-h-[108px] active:scale-[0.98] transition ${TONES[tone]}`}
        >
            <span className={`w-9 h-9 rounded-full bg-white flex items-center justify-center ${TONE_ICONS[tone]}`}>
                <Icon size={18} strokeWidth={1.8} />
            </span>
            <span className="mt-3">
                <span className="block text-base font-bold leading-none">{label}</span>
                <span className="block text-[11px] mt-1 opacity-70">{sub}</span>
            </span>
        </Link>
    );
}
